import PropTypes from 'prop-types';
import { Stack } from '@etg/wings';
import { useTranslation } from '@eti/providers';
import { css } from '@eti/styles';
import { Trip } from '@eti/types';
import { useScrollTo } from '@eti/utils';
import { SelectedFilters } from '../../constants/resultPropTypes';
import LoadMoreButton from './LoadMoreButton';
import NoResultsContent from './NoResultsContent';

const stackStyles = css`
  margin-bottom: 32px;
  margin-top: 32px;
`;

const NoResults = ({
  filteredFlightsCount,
  flightResults,
  flightsCount,
  handleFilter,
  isSearchLoading,
  resetAllFilters,
  selectedFilters,
  setFetchMoreResults,
  toggleSearchPanel,
}) => {
  const { t } = useTranslation();
  const { scrollVertical } = useScrollTo();

  const hasNoFlights = flightsCount === 0;
  const hasNoMoreFlights =
    filteredFlightsCount !== 0 && filteredFlightsCount === flightResults?.length;
  const hasNoFilteredFlights = filteredFlightsCount === 0 && flightsCount > 0;

  const handleResetAll = () => handleFilter(resetAllFilters(selectedFilters));
  const scrollToTop = () => scrollVertical('smooth', 0);

  const shouldShowLoadMoreButton = !hasNoMoreFlights && !hasNoFilteredFlights && !hasNoFlights;

  return (
    <>
      <Stack align="center" className={stackStyles} spacing="16">
        {hasNoFlights && (
          <NoResultsContent
            buttonText={t('Result.ResultTripDetailsHeader.EditSearch.Label')}
            dataTestId="hasNoFlights-button"
            description={t('Result.NoResults.Description.Text')}
            onClick={toggleSearchPanel}
            title={t('Result.NoResults.Title')}
          />
        )}
        {hasNoFilteredFlights && (
          <NoResultsContent
            buttonText={t('Result.SearchFilters.ClearFilters.Button')}
            dataTestId="hasNoFilteredFlights-button"
            description={t('Result.NoFilteredResults.Description.Text')}
            onClick={handleResetAll}
            title={t('Result.NoFilteredResults.Title')}
          />
        )}
        {hasNoMoreFlights && (
          <NoResultsContent
            buttonText={t('Result.BackToTop.Button.Label')}
            dataTestId="hasNoMoreFlights-button"
            description={t(
              'Result.ScrollNoMoreResults.Label',
              flightResults?.length,
              flightResults?.length,
            )}
            onClick={scrollToTop}
            title={t('Result.ScrollNoMoreResults.Title')}
          />
        )}
        {shouldShowLoadMoreButton && (
          <LoadMoreButton
            buttonText={t('Result.LoadMore.Button.Label')}
            isSearchLoading={isSearchLoading}
            setFetchMoreResults={setFetchMoreResults}
          />
        )}
      </Stack>
    </>
  );
};

NoResults.propTypes = {
  filteredFlightsCount: PropTypes.number,
  flightResults: PropTypes.arrayOf(Trip),
  flightsCount: PropTypes.number,
  handleFilter: PropTypes.func,
  isSearchLoading: PropTypes.bool,
  resetAllFilters: PropTypes.func,
  selectedFilters: SelectedFilters,
  setFetchMoreResults: PropTypes.func,
  toggleSearchPanel: PropTypes.func,
};

export default NoResults;
